import { useEffect } from "react";
import axiosInstance from "../api/axiosInstance";

/**
 * Custom hook to capture JWT token from URL parameters and set it as HTTP-only cookie
 * Useful for iframe communication where the parent app passes token via URL
 * 
 * This hook:
 * 1. Checks for accessToken in URL parameters (?accessToken=...)
 * 2. Sends the token to the backend to set it as an HTTP-only cookie
 * 3. Optionally removes the token from URL to keep browser history clean
 * 4. Gets user data from the backend response
 * 
 * Usage: useTokenFromURL({ removeFromUrl: true })
 */
export function useTokenFromURL(options?: { 
  removeFromUrl?: boolean;
}) {
  useEffect(() => {
    try {
      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const accessToken = urlParams.get("accessToken");

      if (accessToken) {
        console.log(
          "[useTokenFromURL] AccessToken captured from URL"
        );

        // Optionally remove token from URL to keep history clean
        if (options?.removeFromUrl !== false) {
          // Remove the accessToken parameter from URL
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete("accessToken");
          window.history.replaceState({}, document.title, newUrl.toString());
          console.log(
            "[useTokenFromURL] AccessToken removed from URL for security"
          );
        }

        // Send token to backend to set as HTTP-only cookie
        setTokenCookieViaBackend(accessToken);
      }
    } catch (error) {
      console.error("[useTokenFromURL] Error processing URL token:", error);
    }
  }, []); // Run once on component mount
}

/**
 * Send token to backend to set it as an HTTP-only cookie
 * The backend will verify the token and set it as a secure HTTP-only cookie
 * Also store token in localStorage for Bearer token authentication
 * This aligns the iframe flow with the normal login flow (both use HTTP-only cookies)
 */
async function setTokenCookieViaBackend(token: string) {
  try {
    // Store token in localStorage for Bearer token authentication
    localStorage.setItem("accessToken", token);
    console.log("[useTokenFromURL] Token stored in localStorage");

    const response = await axiosInstance.post(
      "/api/excelitte-auth/set-cookie",
      { token },
      { withCredentials: true }
    );

    if (response.data.valid && response.data.user) {
      console.log("[useTokenFromURL] Token cookie set. User data:", response.data.user);
      
      // Dispatch event with user data
      const event = new CustomEvent("tokenVerified", {
        detail: { user: response.data.user },
      });
      window.dispatchEvent(event);
    } else {
      console.warn("[useTokenFromURL] Token cookie setup failed:", response.data.message);
    }
  } catch (error) {
    console.error("[useTokenFromURL] Token cookie setup error:", error);
  }
}
